import { initialState, makeListEffects, makeListReducers } from '@/models/utils';
import { bindServices } from '@/services/api';
import { CoreEnums } from '@/utils/domain/constants';

const api = bindServices('connections');
import { message } from 'antd';
import { getIntl } from 'umi';
import { queryAuthorizeConnection, queryValidateConnection } from '../service';
import { queryConnectionPermissions } from './service';
import { filtersConfig } from './utils/filtersConfig';

const { ConnectionType } = CoreEnums;
const namespace = 'connections';

const validateOrAuthorizeConnection = function* (connectionType, connectionId, { call }) {
  if (connectionType === ConnectionType.LinkedIn || connectionType === ConnectionType.AzureGraph) {
    const redirectUri = `${window.location.origin}/connections/callback`;

    const response = yield call(
      queryAuthorizeConnection,
      connectionType,
      connectionId,
      redirectUri,
    );

    let redirectUrl = null;
    if (response && response.redirectUrl) {
      redirectUrl = response.redirectUrl;
    } else if (response && typeof response === 'string') {
      redirectUrl = response;
    } else {
      const intl = getIntl();
      throw new Error(intl.formatMessage({ id: 'global.error.connection.noRedirectUrl' }));
    }

    window.location.href = redirectUrl;

    return { isRedirection: true, redirectUrl };
  } else {
    yield call(queryValidateConnection, connectionId);
    return { isRedirection: false };
  }
};

const extractErrorMessage = (error, intl) => {
  if (error?.data?.message) {
    return error.data.message;
  } else if (error?.response?.data?.message) {
    return error.response.data.message;
  } else if (error?.message) {
    return error.message;
  } else if (typeof error === 'string') {
    return error;
  } else {
    return intl.formatMessage({ id: 'component.errorBoundary.message' });
  }
};

const ConnectionsModel = {
  namespace,
  state: {
    ...initialState,
    filtersConfig,
    connectionPermissions: [],
  },
  effects: {
    ...makeListEffects({
      namespace,
      fetchRoute: 'connection',
      fetchParams: 'connections',
    }),
    *create({ payload }, { call, put }) {
      try {
        const { validateAfterSave, connectionId, connectionType, ...restPayload } = payload;

        const response = yield call(api.create, restPayload);

        const intl = getIntl();
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: `${intl.formatMessage({
              id: 'component.notifications.operationCompleted',
            })} ${intl.formatMessage({ id: 'component.notifications.successfully' })}`,
          },
        });

        if (validateAfterSave && connectionId && connectionType) {
          try {
            const validationResult = yield call(
              validateOrAuthorizeConnection,
              connectionType,
              connectionId,
              { call },
            );

            if (validationResult && validationResult.isRedirection) {
              return response;
            }

            yield put({
              type: 'global/showNotificationsGlobal',
              payload: {
                visible: true,
                type: 'success',
                message: `${intl.formatMessage({
                  id: 'component.notifications.operationCompleted',
                })} ${intl.formatMessage({ id: 'component.notifications.successfully' })}`,
              },
            });
          } catch (validationError) {
            throw validationError;
          }
        }

        yield put({
          type: 'success',
          payload: { success: true },
        });

        return response;
      } catch (error) {
        const intl = getIntl();

        const errorMessage = extractErrorMessage(error, intl);

        if (errorMessage) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: errorMessage,
            },
            validationErrors: error.validationErrors,
          });

          yield put({
            type: 'errors',
            payload: {
              validationErrors: error.validationErrors || [],
              message: errorMessage,
            },
          });
        }

        return null;
      }
    },
    *loadConnectionPermissions({ payload }, { call, put }) {
      try {
        const { connectionType } = payload;
        if (connectionType) {
          const response = yield call(queryConnectionPermissions, connectionType);
          yield put({
            type: 'setConnectionPermissions',
            payload: response,
          });
          return response;
        }
        return [];
      } catch (error) {
        return [];
      }
    },
    *fetchApiData({ payload }, { call, put }) {
      try {
        // Special handling for connectionPermissions endpoint
        if (payload.route?.includes('connectionPermissions')) {
          yield put({
            type: 'global/queryApiData',
            payload: { ...payload, loading: true },
          });

          const connectionTypeFromPayload = payload.params?.connectionType;
          let serverIdFromRoute = null;
          try {
            const parts = payload.route.split('/');
            if (parts.length > 1) {
              serverIdFromRoute = parts[1];
            }
          } catch (e) {}

          if (!connectionTypeFromPayload) {
            yield put({
              type: 'global/queryApiData',
              payload: {
                ...payload,
                data: [],
                loading: false,
              },
            });
            return;
          }

          const response = yield call(
            queryConnectionPermissions,
            connectionTypeFromPayload,
            serverIdFromRoute,
          );

          const transformedData = response.map((permission) => ({
            id: permission.id || permission.value,
            name: permission.name || permission.text || permission.displayText,
            text: permission.name || permission.text || permission.displayText,
            value: permission.id || permission.value,
            label: permission.name || permission.text || permission.displayText,
          }));

          yield put({
            type: 'global/queryApiData',
            payload: {
              ...payload,
              data: transformedData,
              loading: false,
              fetched: true,
            },
          });

          return;
        }

        yield put({
          type: 'global/fetchApiData',
          payload,
        });
      } catch (error) {
        yield put({
          type: 'global/queryApiData',
          payload: {
            ...payload,
            data: [],
            loading: false,
          },
        });
      }
    },
    *validateConnection({ payload }, { call, put }) {
      try {
        const { connectionType, connectionId, params } = payload;
        const result = yield call(validateOrAuthorizeConnection, connectionType, connectionId, {
          call,
        });

        if (result && result.isRedirection) {
          return;
        }

        const intl = getIntl();
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: `${intl.formatMessage({
              id: 'component.notifications.operationCompleted',
            })} ${intl.formatMessage({ id: 'component.notifications.successfully' })}`,
          },
        });

        const response = yield call(api.getAll, {
          route: 'connection/paged',
          params,
        });
        yield put({
          type: 'queryConnections',
          payload: {
            ...response,
            limit: params.limit,
            page: params.page,
          },
        });
      } catch (error) {
        const intl = getIntl();
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: extractErrorMessage(error, intl),
          },
        });
      }
    },
    *authorizeConnection({ payload }, { call }) {
      try {
        const { connectionType, connectionId } = payload;

        const response = yield call(queryAuthorizeConnection, connectionType, connectionId);

        if (response && response.redirectUrl) {
          window.location.href = response.redirectUrl;
        } else if (response && typeof response === 'string') {
          window.location.href = response;
        } else {
          const intl = getIntl();
          throw new Error(intl.formatMessage({ id: 'global.error.connection.noRedirectUrl' }));
        }
      } catch (error) {
        const intl = getIntl();
        message.error(intl.formatMessage({ id: 'component.errorBoundary.message' }));
      }
    },

    *createAndValidateConnection({ payload }, { call, put }) {
      try {
        const { baseData, configData, connectionType } = payload;

        const createPayload = {
          route: 'connection',
          data: baseData,
        };

        const createdConnection = yield call(api.create, createPayload);

        const connectionId =
          createdConnection.id || createdConnection?.data?.id || createdConnection?.result?.id;

        if (!connectionId) {
          const intl = getIntl();
          throw new Error(intl.formatMessage({ id: 'global.error.connection.noConnectionId' }));
        }

        if (configData && Object.keys(configData).length > 0) {
          const configPayload = {
            route: `connection/updateConfig/${connectionType}/${connectionId}`,
            data: configData,
          };

          yield call(api.create, configPayload);
        }

        const validationResult = yield call(
          validateOrAuthorizeConnection,
          connectionType,
          connectionId,
          { call },
        );

        if (validationResult && validationResult.isRedirection) {
          return;
        }

        const intl = getIntl();
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: `${intl.formatMessage({
              id: 'component.notifications.operationCompleted',
            })} ${intl.formatMessage({ id: 'component.notifications.successfully' })}`,
          },
        });

        yield put({
          type: 'success',
          payload: { success: true },
        });
      } catch (error) {
        const intl = getIntl();
        const errorMessage = extractErrorMessage(error, intl);

        yield put({
          type: 'global/showNotificationsForm',
          payload: {
            visible: true,
            type: 'error',
            message: errorMessage,
          },
          validationErrors: error.validationErrors,
        });

        yield put({
          type: 'errors',
          payload: {
            validationErrors: error.validationErrors || [],
            message: errorMessage,
          },
        });

        return null;
      }
    },

    *createConnectionAndConfig({ payload }, { call, put }) {
      try {
        const { baseData, configData, connectionType } = payload;

        const createPayload = {
          route: 'connection',
          data: baseData,
        };

        const createdConnection = yield call(api.create, createPayload);

        const connectionId =
          createdConnection.id || createdConnection?.data?.id || createdConnection?.result?.id;

        if (!connectionId) {
          const intl = getIntl();
          throw new Error(intl.formatMessage({ id: 'global.error.connection.noConnectionId' }));
        }

        if (configData && Object.keys(configData).length > 0) {
          const configPayload = {
            route: `connection/updateConfig/${connectionType}/${connectionId}`,
            data: configData,
          };

          yield call(api.create, configPayload);
        }

        const intl = getIntl();
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: `${intl.formatMessage({
              id: 'component.notifications.operationCompleted',
            })} ${intl.formatMessage({ id: 'component.notifications.successfully' })}`,
          },
        });

        yield put({
          type: 'success',
          payload: { success: true },
        });
      } catch (error) {
        const intl = getIntl();
        const errorMessage = extractErrorMessage(error, intl);

        yield put({
          type: 'global/showNotificationsForm',
          payload: {
            visible: true,
            type: 'error',
            message: errorMessage,
          },
          validationErrors: error.validationErrors,
        });

        yield put({
          type: 'errors',
          payload: {
            validationErrors: error.validationErrors || [],
            message: errorMessage,
          },
        });

        return null;
      }
    },

    *processCallback({ payload }, { call, put }) {
      try {
        const { callbackData } = payload;

        if (callbackData.error) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: callbackData.errorDescription || callbackData.error,
            },
          });
          return;
        }

        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (!guidRegex.test(callbackData.state)) {
          throw new Error(
            getIntl().formatMessage({ id: 'global.error.connection.invalidStateFormat' }),
          );
        }

        yield call(queryValidateConnection, null, {
          code: callbackData.code,
          state: callbackData.state,
          redirectUri: callbackData.redirectUri,
        });

        const intl = getIntl();
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: `${intl.formatMessage({
              id: 'component.notifications.operationCompleted',
            })} ${intl.formatMessage({ id: 'component.notifications.successfully' })}`,
          },
        });

        yield put({
          type: 'fetch',
          payload: {
            page: 1,
            limit: 10,
          },
        });
      } catch (err) {
        const intl = getIntl();
        const errorMessage = extractErrorMessage(err, intl);

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: errorMessage,
          },
        });
      }
    },

    *fetchItemConfig({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: 'connection',
          id: payload.id,
          params: {
            select: 'id,type,configurations',
          },
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {
        console.error('Error fetching item config:', e);
      }
    },
  },
  reducers: {
    ...makeListReducers(namespace),
    setConnectionPermissions(state, { payload }) {
      return {
        ...state,
        connectionPermissions: payload,
      };
    },
    errors(state, { payload }) {
      return {
        ...state,
        loading: false,
        validationErrors: payload.validationErrors || [],
      };
    },
    failed(state, { payload }) {
      return {
        ...state,
        loading: false,
        validationErrors: payload.validationErrors || [],
      };
    },
  },
};

export default ConnectionsModel;
